import clsx from 'clsx';
import { Popover, PopoverContent, PopoverTrigger, Button } from '@artstorefronts/ui';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useTokens, UNLIMITED_SPARK_LIMIT } from '../context/tokens';

export function SparkUsage({
  className,
  mode = 'light',
  titleAlign = 'left',
  showTitle = true,
  position = 'top',
}: {
  className?: string;
  mode?: 'light' | 'dark';
  showTitle?: boolean;
  titleAlign?: 'left' | 'right';
  position?: 'top' | 'right' | 'bottom' | 'left';
}) {
  const { t } = useTranslation('billing');
  const { spark_tokens, spark_limit } = useTokens() as any;
  const spark_percentage = spark_limit === UNLIMITED_SPARK_LIMIT ? 0 : ((spark_tokens || 0) / (spark_limit || 1)) * 100;

  const themeColors = {
    light: {
      hover: 'hover:bg-white/15',
      text: 'text-white/70',
      bar: 'bg-white/30',
      progress: 'bg-white',
    },
    dark: {
      hover: 'hover:bg-black/5',
      text: 'text-black',
      bar: 'bg-black/20',
      progress: 'bg-black',
    },
  };

  if (spark_limit === UNLIMITED_SPARK_LIMIT) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={clsx(
            'w-full rounded bg-transparent transition-colors duration-200 p-2 text-left',
            themeColors[mode].hover,
            className
          )}
        >
          <div className="flex flex-col gap-1">
            {showTitle && (
              <span
                className={clsx(
                  'text-[10px] uppercase',
                  themeColors[mode].text,
                  titleAlign === 'right' && 'text-right'
                )}
              >
                {t('spark-usage.title')}
              </span>
            )}
            <div className="flex justify-between items-center gap-2">
              <div className={clsx('relative flex-grow h-1.5 rounded-full overflow-hidden', themeColors[mode].bar)}>
                <div
                  className={clsx(
                    'absolute left-0 top-0 h-full rounded-full transition-all duration-500 ease-out',
                    themeColors[mode].progress
                  )}
                  style={{ width: `${spark_percentage}%` }}
                />
              </div>
              <p className={clsx('text-xs font-light ml-2 whitespace-nowrap', themeColors[mode].text)}>
                {Math.min(spark_tokens || 0, spark_limit || 0)} / {spark_limit || 0}
              </p>
            </div>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent side={position} className="w-64">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">{t('spark-usage.need-more')}</h4>
            <p className="text-sm text-muted-foreground">{t('spark-usage.upgrade-description')}</p>
          </div>
          <Button asChild className="w-full">
            <Link to="/plans">{t('spark-usage.upgrade')}</Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
